// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import AddToListModal from "@insite/content-library/Components/AddToListModal";
import ContentPage from "@insite/content-library/Pages/Page";
import MobiusPage from "@insite/mobius/Page";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import declineUserAccount from "@mcmc/App/Handlers/Account/DeclineUserAccount";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import Notify from "@mcmc/XDL/Components/Notify";
import cloneDeep from "lodash/cloneDeep";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const fieldDefinitions = cloneDeep(ContentPage.definition.fieldDefinitions);

const mapDispatchToProps = {
    declineUserAccount,
};

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    let token;
    if (location && location.search) {
        const parsedQuery = parseQueryString<{ token: string }>(location.search);
        token = parsedQuery.token;
    }

    return {
        token,
    };
};

type OwnProps = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

interface DeclineAccountMessage {
    type: "info" | "danger" | "success";
    text: string;
}

interface DeclineAccountPageStyles {
    notifyWrapper: InjectableCss;
    spinner: { typographyProps: TypographyPresentationProps };
}

const styles: DeclineAccountPageStyles = {
    notifyWrapper: {
        css: css`
            margin: 20px 0;
        `,
    },
    spinner: {
        typographyProps: {
            size: 14,
            lineHeight: "18px",
            weight: "normal",
            css: css`
                text-transform: none;
                margin-bottom: 0;
            `,
        },
    },
};

const DeclineAccountPage = ({ id, declineUserAccount, token }: OwnProps) => {
    const [message, setMessage] = useState<DeclineAccountMessage>({ type: "info", text: translate("Processing") });

    useEffect(() => {
        if (token) {
            declineUserAccount({
                token: `${token}`,
                onError: erorMessage => setMessage({ type: "danger", text: erorMessage }),
                onSuccess: result => {
                    setMessage({ type: "success", text: result });
                },
            });
        } else {
            setMessage({ type: "danger", text: translate("No valid token provided") });
        }
    }, []);

    return (
        <MobiusPage>
            <Zone contentId={id} zoneName="Top-Content" requireRows />
            <StyledWrapper {...styles.notifyWrapper}>
                <Notify hideCloseButton={true} messageType={message.type}>
                    <EllipsisSpinner
                        label={message.text}
                        showSpinner={message.type === "info"}
                        {...styles.spinner}
                    ></EllipsisSpinner>
                </Notify>
            </StyledWrapper>
            <Zone contentId={id} zoneName="Bottom-Content" requireRows />
            <AddToListModal />
        </MobiusPage>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DeclineAccountPage),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
        fieldDefinitions,
    },
};

export default pageModule;
